import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DEFAULT_CATEGORIES } from "../../lib/constants";
import { getBrand, pubFromHostname } from "../../lib/brand";
import { api, formatApiErrorDetail, setSubmissionAccessToken } from "../../lib/api";
import BrandHeader from "../../components/PublicHeader";
import SiteFooter from "../../components/SiteFooter";
import { toast } from "sonner";
import { Plus, X, ArrowRight } from "lucide-react";

const STORY_TYPES = [
  { key: "elopement", label: "Elopement" },
  { key: "engagement", label: "Engagement" },
  { key: "family", label: "Family" },
  { key: "love_story", label: "Love Story" },
  { key: "proposal", label: "Proposal" },
  { key: "styled_shoot", label: "Styled Shoot" },
  { key: "wedding", label: "Wedding" },
];

const PARTNER_OPTS = [
  { value: "both", label: "Both" },
  { value: "dwh", label: "Dancing With Her" },
  { value: "dwt", label: "Dancing With Them" },
  { value: "neither", label: "Neither" },
];

export default function ProSubmit() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryPub = params.get("pub") === "dwt" ? "dwt" : params.get("pub") === "dwh" ? "dwh" : null;
  const [hostPub, setHostPub] = useState(null);
  const initPub = queryPub || pubFromHostname({});
  const [pubPref, setPubPref] = useState(initPub);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [pro, setPro] = useState({ name: "", email: "", business_name: "", vendor_category: "", instagram: "", website: "", partner_status: "neither" });
  const [storyCategory, setStoryCategory] = useState("");
  const [description, setDescription] = useState("");
  const [eventName, setEventName] = useState("");
  const [galleryLink, setGalleryLink] = useState("");
  const [galleryPassword, setGalleryPassword] = useState("");
  const [mailingOptIn, setMailingOptIn] = useState(false);
  const [partnershipOptIn, setPartnershipOptIn] = useState(false);
  const [photoPermission, setPhotoPermission] = useState(false);
  const [photographer, setPhotographer] = useState({ name: "", business_name: "", email: "" });

  useEffect(() => {
    getBrand().then((brand) => setHostPub(pubFromHostname(brand || {})));
  }, []);
  const [loversNames, setLoversNames] = useState(["", ""]);
  const [loversEmail, setLoversEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [invitedDone, setInvitedDone] = useState(false);

  useEffect(() => {
    api.get("/meta/categories").then((r) => setCategories(r.data.categories || DEFAULT_CATEGORIES)).catch(() => {});
  }, []);
  const brandPub = pubPref === "dwt" ? "dwt" : pubPref === "dwh" ? "dwh" : hostPub;
  useEffect(() => {
    if (brandPub) document.documentElement.dataset.pub = brandPub;
    return () => { delete document.documentElement.dataset.pub; };
  }, [brandPub]);

  const isPhotographer = pro.vendor_category.trim().toLowerCase() === "photographer";
  const isStyled = storyCategory === "styled_shoot";
  const setP = (k, v) => setPro((p) => ({ ...p, [k]: v }));
  const setPh = (k, v) => setPhotographer((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!pubPref) return toast.error("Please choose your preferred publication.");
    if (!pro.name.trim() || !pro.business_name.trim()) return toast.error("Please add your name and business name.");
    if (!pro.email.trim()) return toast.error("Please add your email.");
    if (!pro.vendor_category) return toast.error("Please choose your vendor category.");
    if (!storyCategory) return toast.error("Please choose the story type.");
    if (!description.trim()) return toast.error("Tell us about the story you're sharing.");
    if (isPhotographer && !photoPermission) return toast.error("Please confirm permission to publish these images.");
    if (!isPhotographer && (!photographer.name.trim() || !photographer.email.trim()))
      return toast.error("Please add the photographer's name and email.");
    if (!isStyled) {
      const names = loversNames.filter((n) => n.trim());
      if (!names.length) return toast.error("Please add the lovers' names.");
      if (!loversEmail.trim()) return toast.error("Please add the lovers' primary email.");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/pro/submissions", {
        publication_preference: pubPref,
        pro: { ...pro, instagram: pro.instagram.replace(/^@/, "") },
        story_category: storyCategory,
        story_description: description.trim(),
        event_name: eventName.trim(),
        gallery_link: galleryLink.trim(),
        gallery_password: galleryPassword,
        mailing_opt_in: mailingOptIn,
        partnership_opt_in: partnershipOptIn,
        photographer_permission_granted: photoPermission,
        photographer: isPhotographer ? undefined : photographer,
        lovers_names: isStyled ? [] : loversNames.filter((n) => n.trim()),
        lovers_email: isStyled ? "" : loversEmail.trim(),
      });
      if (data.mode === "continue") {
        setSubmissionAccessToken(data.event_id, data.access_token);
        navigate(`/create-event/${data.event_id}/vendors`);
      } else {
        setInvitedDone(true);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (invitedDone) {
    return (
      <div className="min-h-screen bg-[#f5f3ed] flex flex-col" data-testid="pro-invited-page">
        <BrandHeader pub={brandPub} />
        <div className="max-w-2xl w-full mx-auto px-6 sm:px-10 py-32 text-center flex-1">
          <p className="btw-overline mb-8">Invite sent</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight mb-6">
            The <strong>lovers</strong> have the pen from here.
          </h1>
          <p className="text-[#5c5852] leading-relaxed max-w-md mx-auto">
            We've emailed them a secure link to confirm the vendor credits and tell their
            side of the story. We'll take it from there.
          </p>
        </div>
        <SiteFooter pub={brandPub} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f3ed] flex flex-col" data-testid="pro-submit-page" data-pub={brandPub || "neutral"}>
      <BrandHeader pub={brandPub} />

      <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-16" role="main">
        <p className="btw-overline mb-5">For wedding pros</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight mb-10">
          Share a <strong>story</strong> you brought to life.
        </h1>

        <form onSubmit={submit}>
          {/* Publication preference */}
          <div className="mb-8" data-testid="pro-section-publication">
            <p className="btw-step-label">SELECT A PUBLICATION</p>
            <p className="btw-step-desc">Where would you prefer your story to be published?</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { v: "dwh", label: "Dancing With Her" },
                { v: "dwt", label: "Dancing With Them" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setPubPref(o.v)}
                  data-testid={`pro-pub-${o.v}`}
                  className={`pub-btn pub-btn-${o.v} ${pubPref === o.v ? "active" : ""}`}
                >
                  <span
                    className="pub-label text-xs font-bold uppercase tracking-widest"
                    style={{ color: o.v === "dwt" && pubPref === "dwt" ? "#fff" : "#000" }}
                  >
                    {o.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* About you */}
          <div className="mb-8" data-testid="pro-section-you">
            <p className="btw-step-label">ABOUT YOU</p>
            <div className="btw-section grid sm:grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <label className="text-xs font-bold block mb-1">Your name</label>
                <input data-testid="pro-name" className="btw-input" value={pro.name} onChange={(e) => setP("name", e.target.value)}  aria-label="Your name"/>
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Your email</label>
                <input data-testid="pro-email" type="email" className="btw-input" value={pro.email} onChange={(e) => setP("email", e.target.value)}  aria-label="Your email"/>
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Business name</label>
                <input data-testid="pro-business" className="btw-input" value={pro.business_name} onChange={(e) => setP("business_name", e.target.value)}  aria-label="Business name"/>
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Vendor category</label>
                <div className="btw-select-wrap">
                  <select data-testid="pro-category" className="btw-input btw-input-select bg-transparent appearance-none" value={pro.vendor_category} onChange={(e) => setP("vendor_category", e.target.value)} aria-label="Vendor category">
                    <option value="">Select</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Instagram</label>
                <div className="btw-ig">
                  <span className="text-black">@</span>
                  <input data-testid="pro-instagram" aria-label="Instagram" className="btw-input pl-1" value={pro.instagram} onChange={(e) => setP("instagram", e.target.value.replace(/^@/, ""))} />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Website</label>
                <input data-testid="pro-website" className="btw-input" value={pro.website} onChange={(e) => setP("website", e.target.value)}  aria-label="Website"/>
              </div>
            </div>
          </div>

          {/* Partner status */}
          <div className="mb-8" data-testid="pro-section-partner">
            <p className="btw-step-label">PARTNERSHIP</p>
            <p className="btw-step-desc">Are you a current paid partner with Dancing With Her, Dancing With Them, both or neither?</p>
            <div className="btw-section grid sm:grid-cols-4 gap-3">
              {PARTNER_OPTS.map((o) => (
                <label key={o.value} className="flex items-center gap-2 text-sm text-black cursor-pointer">
                  <input
                    type="radio"
                    name="partner_status"
                    data-testid={`pro-partner-${o.value}`}
                    className="btw-check"
                    checked={pro.partner_status === o.value}
                    onChange={() => setP("partner_status", o.value)}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>

          {/* The story */}
          <div className="mb-8" data-testid="pro-section-story">
            <p className="btw-step-label">THE STORY</p>
            <div className="btw-section space-y-5">
              <div>
                <label className="text-xs font-bold block mb-1">Story type</label>
                <div className="btw-select-wrap">
                  <select data-testid="pro-story-type" className="btw-input btw-input-select bg-transparent appearance-none" value={storyCategory} onChange={(e) => setStoryCategory(e.target.value)} aria-label="Story type">
                    <option value="">Select</option>
                    {STORY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Event name (optional)</label>
                <input data-testid="pro-event-name" className="btw-input" placeholder={isStyled ? "e.g. Autumn editorial shoot" : "e.g. Casey & Riley"} value={eventName} onChange={(e) => setEventName(e.target.value)}  aria-label="Event name (optional)"/>
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Tell us about this story and why you're sharing it</label>
                <textarea data-testid="pro-description" rows={4} className="btw-input resize-none" value={description} onChange={(e) => setDescription(e.target.value)}  aria-label="Tell us about this story and why you're sharing it"/>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-bold block mb-1">Gallery link (optional)</label>
                  <input data-testid="pro-gallery-link" type="url" className="btw-input" placeholder="https://…" value={galleryLink} onChange={(e) => setGalleryLink(e.target.value)} aria-label="Gallery link (optional)" />
                </div>
                <div>
                  <label className="text-xs font-bold block mb-1">Gallery password (optional)</label>
                  <input data-testid="pro-gallery-password" className="btw-input" value={galleryPassword} onChange={(e) => setGalleryPassword(e.target.value)} aria-label="Gallery password (optional)" />
                </div>
              </div>
            </div>
          </div>

          {/* Photographer permission */}
          <div className="mb-8" data-testid="pro-section-photographer">
            <p className="btw-step-label">IMAGES</p>
            <div className="btw-section">
              {isPhotographer ? (
                <label className="flex items-start gap-3 text-sm text-black cursor-pointer">
                  <input type="checkbox" data-testid="pro-photo-permission" className="btw-check mt-0.5" checked={photoPermission} onChange={(e) => setPhotoPermission(e.target.checked)} />
                  <span><strong>I give permission to publish these images</strong> on the publication and its social channels. (required)</span>
                </label>
              ) : (
                <div>
                  <p className="text-xs text-[#5c5852] mb-4">
                    You're not the photographer, so we'll follow up with them for image permission.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-x-6 gap-y-5">
                    <div>
                      <label className="text-xs font-bold block mb-1">Photographer's name</label>
                      <input data-testid="photographer-name" aria-label="Photographer's name" className="btw-input" value={photographer.name} onChange={(e) => setPh("name", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs font-bold block mb-1">Photographer's business</label>
                      <input data-testid="photographer-business" className="btw-input" value={photographer.business_name} onChange={(e) => setPh("business_name", e.target.value)} aria-label="Photographer's business" />
                    </div>
                    <div>
                      <label className="text-xs font-bold block mb-1">Photographer's email</label>
                      <input data-testid="photographer-email" type="email" className="btw-input" value={photographer.email} onChange={(e) => setPh("email", e.target.value)} aria-label="Photographer's email" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lovers (not styled shoot) */}
          {!isStyled && (
            <div className="mb-8" data-testid="pro-section-lovers">
              <p className="btw-step-label">THE LOVERS</p>
              <p className="btw-step-desc">We'll email them a secure link to confirm the credits and tell their side of the story.</p>
              <div className="btw-section space-y-5">
                {loversNames.map((n, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <input
                      data-testid={`lover-name-${i}`}
                      aria-label={`Lover ${i + 1} name`}
                      className="btw-input flex-1"
                      placeholder={`Lover ${i + 1} name`}
                      value={n}
                      onChange={(e) => setLoversNames((a) => a.map((x, idx) => idx === i ? e.target.value : x))}
                    />
                    {loversNames.length > 1 && (
                      <button type="button" aria-label={`Remove lover ${i + 1}`} onClick={() => setLoversNames((a) => a.filter((_, idx) => idx !== i))} data-testid={`lover-remove-${i}`} className="text-black hover:text-[#b35c5c]">
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                {loversNames.length < 5 && (
                  <button type="button" onClick={() => setLoversNames((a) => [...a, ""])} data-testid="lover-add" className="inline-flex items-center gap-2 text-sm font-bold text-black hover:opacity-70">
                    <Plus size={14} /> Add another lover
                  </button>
                )}
                <div>
                  <label className="text-xs font-bold block mb-1">Lovers' primary email</label>
                  <input data-testid="lovers-email" type="email" aria-label="Lovers' email" className="btw-input" value={loversEmail} onChange={(e) => setLoversEmail(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Opt-ins */}
          <div className="mb-8" data-testid="pro-section-optins">
            <div className="btw-section space-y-4">
              <label className="flex items-center gap-2 text-sm text-black cursor-pointer">
                <input type="checkbox" data-testid="pro-mailing-optin" className="btw-check" checked={mailingOptIn} onChange={(e) => setMailingOptIn(e.target.checked)}  aria-label="Lovers' primary email"/>
                Add me to the mailing list.
              </label>
              <label className="flex items-center gap-2 text-sm text-black cursor-pointer">
                <input type="checkbox" data-testid="pro-partnership-optin" className="btw-check" checked={partnershipOptIn} onChange={(e) => setPartnershipOptIn(e.target.checked)} />
                Tell me about partnerships.
              </label>
            </div>
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={submitting} data-testid="pro-submit" className="btw-btn w-full sm:w-auto">
              <span>{submitting ? "Sending…" : isStyled ? "Continue to vendor credits" : "Send the invite"}</span>
              <span className="btw-btn-arrow"><ArrowRight size={12} /></span>
            </button>
          </div>
        </form>
      </div>

      <SiteFooter pub={brandPub} />
    </div>
  );
}
